import {
  StorageFeeInvoiceListItem,
  StorageFeeOverrides,
  StorageFeeSettings,
} from '@models/resources/storage-fee.model';

/**
 * Fixtures für S3-9 (Lagergebühren-Verwaltung Admin).
 *
 * **Aurum-Defaults sind Platzhalter** (TODO U-12) — finale Werte
 * (Jahresprozentsatz, Standard-Intervall, Stichtag, Rundungsregel,
 * Pro-Rata-Verhalten) sind in der laufenden Konzept-Klärung. Bei der
 * Sprint-3-Demo werden diese Werte explizit als Vorschlag präsentiert.
 *
 * Berechnungs-Annahmen im Fixture (matched ADR-003):
 *  - Formel:   amount = (annual_percentage / 12) * interval_months * depot_value
 *  - Rundung:  kaufmännisch auf 2 Nachkommastellen (D1)
 *  - Periode:  monatsgenau, kein pro-rata (D2)
 *  - Snapshot: depot_value_snapshot_eur entspricht dem Kunden-Portfolio
 *              zum period_end (D3); im Fixture vereinfacht = aktueller
 *              Portfolio-Wert aus `admin-customers.fixtures.ts`.
 *
 * Customer 5 (Berger, customer_since 2025-11-30) hat **bewusst keine**
 * Lagergebühr-Fixtures — demonstriert ADR-003 D6 (Future-Start: erste
 * Periode wird übersprungen, bis period_end ≤ today).
 */

// TODO U-12: Aurum-finale Defaults bestätigen (Intervall, Stichtag, %-p.a., Rundung).
export const DEMO_STORAGE_FEE_SETTINGS_DEFAULTS: StorageFeeSettings = {
  default_interval_months: 12,
  default_start_date: '2026-01-01',
  default_annual_percentage: 0.01, // 1,00 % p. a.
};

/**
 * Pro-Kunden-Overrides. `null` an einer Stelle ⇒ globalen Default
 * verwenden. Werte ebenfalls Platzhalter bis Aurum-Klärung.
 */
export const DEMO_STORAGE_FEE_OVERRIDES: ReadonlyMap<number, StorageFeeOverrides> = new Map([
  // Kunde 1 (Holzner) – nutzt Defaults
  [1, { interval_months: null, start_date: null, annual_percentage: null }],
  // Kunde 2 (Weinberger, Großkunde) – Rabatt auf 0,75 % p. a.
  [2, { interval_months: null, start_date: null, annual_percentage: 0.0075 }],
  // Kunde 3 (Gasteiger) – nutzt Defaults
  [3, { interval_months: null, start_date: null, annual_percentage: null }],
  // Kunde 4 (Klug) – halbjährliche Abrechnung
  [4, { interval_months: 6, start_date: null, annual_percentage: null }],
  // Kunde 5 (Berger) – nutzt Defaults; Future-Start (2025-11-30 + 12 M > today)
  [5, { interval_months: null, start_date: null, annual_percentage: null }],
  // Kunde 6 (Mayrhofer, inaktiv) – historische Rechnungen, aktuell keine Generierung
  [6, { interval_months: null, start_date: null, annual_percentage: null }],
]);

const customerStub = (
  id: number,
  first_name: string,
  last_name: string,
  depot_number: string,
) => ({ id, first_name, last_name, depot_number });

const HOLZNER = customerStub(1, 'Maximilian', 'Holzner', 'GS-2024-00127');
const WEINBERGER = customerStub(2, 'Elena', 'Weinberger', 'GS-2023-00098');
const GASTEIGER = customerStub(3, 'Johannes', 'Gasteiger', 'GS-2024-00153');
const KLUG = customerStub(4, 'Sabine', 'Klug', 'GS-2022-00041');
const MAYRHOFER = customerStub(6, 'Ulrich', 'Mayrhofer', 'GS-2021-00008');

/**
 * 14 erzeugte Lagergebühr-Rechnungen quer über aktive Kunden.
 *
 * Mix aus `paid` (älter) und `sent` (jüngerer Jahrgang) demonstriert
 * Filter, Mark-Paid, Mark-Unpaid, Bulk-Aktion und Historie pro Kunde.
 * Beträge sind formel-konsistent (siehe Datei-Header).
 */
export const DEMO_STORAGE_FEE_INVOICES: StorageFeeInvoiceListItem[] = [
  // --- Kunde 1 Holzner (12 M, 1,00 %, Depot 412.835,40) ---------------
  {
    id: 6001,
    customer_id: 1,
    invoice_number: 'RE-2025-010012',
    amount_eur: 4128.35,
    period_start: '2024-03-12',
    period_end: '2025-03-11',
    depot_value_snapshot_eur: 412_835.4,
    applied_percentage: 0.01,
    applied_interval_months: 12,
    sent_at: '2025-03-12T03:18:00+01:00',
    paid_at: '2025-04-02T11:42:00+02:00',
    generated_at: '2025-03-12T03:00:00+01:00',
    customer: HOLZNER,
  },
  {
    id: 6002,
    customer_id: 1,
    invoice_number: 'RE-2026-010014',
    amount_eur: 4128.35,
    period_start: '2025-03-12',
    period_end: '2026-03-11',
    depot_value_snapshot_eur: 412_835.4,
    applied_percentage: 0.01,
    applied_interval_months: 12,
    sent_at: '2026-03-12T03:18:00+01:00',
    paid_at: null,
    generated_at: '2026-03-12T03:00:00+01:00',
    customer: HOLZNER,
  },

  // --- Kunde 2 Weinberger (12 M, 0,75 %, Depot 1.102.004,20) ----------
  {
    id: 6003,
    customer_id: 2,
    invoice_number: 'RE-2024-010008',
    amount_eur: 8265.03,
    period_start: '2023-06-21',
    period_end: '2024-06-20',
    depot_value_snapshot_eur: 1_102_004.2,
    applied_percentage: 0.0075,
    applied_interval_months: 12,
    sent_at: '2024-06-21T03:18:00+02:00',
    paid_at: '2024-07-15T09:30:00+02:00',
    generated_at: '2024-06-21T03:00:00+02:00',
    customer: WEINBERGER,
  },
  {
    id: 6004,
    customer_id: 2,
    invoice_number: 'RE-2025-010019',
    amount_eur: 8265.03,
    period_start: '2024-06-21',
    period_end: '2025-06-20',
    depot_value_snapshot_eur: 1_102_004.2,
    applied_percentage: 0.0075,
    applied_interval_months: 12,
    sent_at: '2025-06-21T03:18:00+02:00',
    paid_at: '2025-07-09T14:12:00+02:00',
    generated_at: '2025-06-21T03:00:00+02:00',
    customer: WEINBERGER,
  },

  // --- Kunde 3 Gasteiger (12 M, 1,00 %, Depot 58.410,00) -------------
  {
    id: 6005,
    customer_id: 3,
    invoice_number: 'RE-2025-010024',
    amount_eur: 584.1,
    period_start: '2024-09-04',
    period_end: '2025-09-03',
    depot_value_snapshot_eur: 58_410.0,
    applied_percentage: 0.01,
    applied_interval_months: 12,
    sent_at: '2025-09-04T03:18:00+02:00',
    paid_at: null,
    generated_at: '2025-09-04T03:00:00+02:00',
    customer: GASTEIGER,
  },

  // --- Kunde 4 Klug (6 M, 1,00 %, Depot 742.188,70) ------------------
  // Halbjährliche Periode ⇒ amount = 0,01 / 12 * 6 * 742_188,70 = 3710,94
  {
    id: 6006,
    customer_id: 4,
    invoice_number: 'RE-2023-010003',
    amount_eur: 3710.94,
    period_start: '2022-11-02',
    period_end: '2023-05-01',
    depot_value_snapshot_eur: 742_188.7,
    applied_percentage: 0.01,
    applied_interval_months: 6,
    sent_at: '2023-05-02T03:18:00+02:00',
    paid_at: '2023-05-29T10:11:00+02:00',
    generated_at: '2023-05-02T03:00:00+02:00',
    customer: KLUG,
  },
  {
    id: 6007,
    customer_id: 4,
    invoice_number: 'RE-2023-010009',
    amount_eur: 3710.94,
    period_start: '2023-05-02',
    period_end: '2023-11-01',
    depot_value_snapshot_eur: 742_188.7,
    applied_percentage: 0.01,
    applied_interval_months: 6,
    sent_at: '2023-11-02T03:18:00+01:00',
    paid_at: '2023-11-22T15:45:00+01:00',
    generated_at: '2023-11-02T03:00:00+01:00',
    customer: KLUG,
  },
  {
    id: 6008,
    customer_id: 4,
    invoice_number: 'RE-2024-010014',
    amount_eur: 3710.94,
    period_start: '2023-11-02',
    period_end: '2024-05-01',
    depot_value_snapshot_eur: 742_188.7,
    applied_percentage: 0.01,
    applied_interval_months: 6,
    sent_at: '2024-05-02T03:18:00+02:00',
    paid_at: '2024-06-04T08:50:00+02:00',
    generated_at: '2024-05-02T03:00:00+02:00',
    customer: KLUG,
  },
  {
    id: 6009,
    customer_id: 4,
    invoice_number: 'RE-2024-010022',
    amount_eur: 3710.94,
    period_start: '2024-05-02',
    period_end: '2024-11-01',
    depot_value_snapshot_eur: 742_188.7,
    applied_percentage: 0.01,
    applied_interval_months: 6,
    sent_at: '2024-11-02T03:18:00+01:00',
    paid_at: '2024-11-30T12:18:00+01:00',
    generated_at: '2024-11-02T03:00:00+01:00',
    customer: KLUG,
  },
  {
    id: 6010,
    customer_id: 4,
    invoice_number: 'RE-2025-010029',
    amount_eur: 3710.94,
    period_start: '2024-11-02',
    period_end: '2025-05-01',
    depot_value_snapshot_eur: 742_188.7,
    applied_percentage: 0.01,
    applied_interval_months: 6,
    sent_at: '2025-05-02T03:18:00+02:00',
    paid_at: null,
    generated_at: '2025-05-02T03:00:00+02:00',
    customer: KLUG,
  },
  {
    id: 6011,
    customer_id: 4,
    invoice_number: 'RE-2025-010036',
    amount_eur: 3710.94,
    period_start: '2025-05-02',
    period_end: '2025-11-01',
    depot_value_snapshot_eur: 742_188.7,
    applied_percentage: 0.01,
    applied_interval_months: 6,
    sent_at: '2025-11-02T03:18:00+01:00',
    paid_at: null,
    generated_at: '2025-11-02T03:00:00+01:00',
    customer: KLUG,
  },

  // --- Kunde 6 Mayrhofer (12 M, 1,00 %, Depot 234.900,00 — inaktiv) --
  // Historische Rechnungen bleiben sichtbar, auch wenn der Kunde aktuell
  // deaktiviert ist (Audit-/Buchhaltungs-Sicht).
  {
    id: 6012,
    customer_id: 6,
    invoice_number: 'RE-2023-010001',
    amount_eur: 2349.0,
    period_start: '2022-02-14',
    period_end: '2023-02-13',
    depot_value_snapshot_eur: 234_900.0,
    applied_percentage: 0.01,
    applied_interval_months: 12,
    sent_at: '2023-02-14T03:18:00+01:00',
    paid_at: '2023-03-08T09:15:00+01:00',
    generated_at: '2023-02-14T03:00:00+01:00',
    customer: MAYRHOFER,
  },
  {
    id: 6013,
    customer_id: 6,
    invoice_number: 'RE-2024-010005',
    amount_eur: 2349.0,
    period_start: '2023-02-14',
    period_end: '2024-02-13',
    depot_value_snapshot_eur: 234_900.0,
    applied_percentage: 0.01,
    applied_interval_months: 12,
    sent_at: '2024-02-14T03:18:00+01:00',
    paid_at: '2024-03-04T11:45:00+01:00',
    generated_at: '2024-02-14T03:00:00+01:00',
    customer: MAYRHOFER,
  },
  {
    id: 6014,
    customer_id: 6,
    invoice_number: 'RE-2025-010011',
    amount_eur: 2349.0,
    period_start: '2024-02-14',
    period_end: '2025-02-13',
    depot_value_snapshot_eur: 234_900.0,
    applied_percentage: 0.01,
    applied_interval_months: 12,
    sent_at: '2025-02-14T03:18:00+01:00',
    paid_at: null,
    generated_at: '2025-02-14T03:00:00+01:00',
    customer: MAYRHOFER,
  },
];
